import { useState, useEffect, useRef, type ChangeEvent, type TouchEvent } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { api, WEB_ORIGIN, contributionApi } from '../../api/client';
import type { CarouselEvent, Member, EventPhoto } from '../../types';
import { AudioRecorder, audioFileName } from '../../components/AudioRecorder';
import { ShareEventModal } from '../../components/ShareEventModal';
import { ReportOrBlockModal } from '../../components/ReportOrBlockModal';
import { useFaceDetection, preloadFaceModels } from '../../hooks/useFaceDetection';
import { useAuth } from '../../hooks/useAuth';
import { format } from 'date-fns';
import { parseEventDate } from '../../utils/eventDate';
import { runWithConcurrency } from '../../utils/concurrencyPool';
import { getEventTemplate } from '../../utils/eventTemplates';
import { extractPhotoMetadata } from '../../utils/extractPhotoMetadata';
import { generatePhotoCaption } from '../../utils/generatePhotoCaption';
import { readEventNav } from '../../utils/eventNav';
import { PHOTO_ACCEPT, serverErrorMessage, describeUploadFailures } from '../../utils/photoUpload';

const MAX_PHOTOS_PER_ADD = 10;

interface DetailFace {
  cropUrl: string;
  assignedUserId: string | null;
}

// Previous/Next swaps only the :id param, which would otherwise keep this
// component mounted with the last event's open modals, drafts and face state.
// Keying by id gives every event a fresh page.
export function EventDetail() {
  const { id } = useParams<{ id: string }>();
  return <EventDetailPage key={id} />;
}

/** Elements a horizontal swipe must not hijack: text entry, media scrubbers,
 * and anything that scrolls sideways on its own. */
function swipeBlockedAt(target: EventTarget | null): boolean {
  let el = target instanceof Element ? target : null;
  if (el?.closest('input, textarea, select, audio, video, [contenteditable="true"], [data-no-swipe]')) return true;
  while (el && el !== document.body) {
    if (el.scrollWidth > el.clientWidth + 1 && /(auto|scroll)/.test(getComputedStyle(el).overflowX)) return true;
    el = el.parentElement;
  }
  return false;
}

function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const navItems = readEventNav(location.state);
  const navIndex = navItems ? navItems.findIndex(item => item.id === id) : -1;
  const prevItem = navItems && navIndex > 0 ? navItems[navIndex - 1] : null;
  const nextItem = navItems && navIndex >= 0 && navIndex < navItems.length - 1 ? navItems[navIndex + 1] : null;
  const swipeStart = useRef<{ x: number; y: number; t: number } | null>(null);
  const { user } = useAuth();
  const [event, setEvent] = useState<CarouselEvent | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingLabelId, setEditingLabelId] = useState<string | null>(null);
  const [editingLabelValue, setEditingLabelValue] = useState('');
  const [editingCaptionId, setEditingCaptionId] = useState<string | null>(null);
  const [editingCaptionValue, setEditingCaptionValue] = useState('');
  const [pickerOpenPhotoId, setPickerOpenPhotoId] = useState<string | null>(null);
  const [pickerFaceIdx, setPickerFaceIdx] = useState<number | null>(null);
  const [photoFaces, setPhotoFaces] = useState<Record<string, DetailFace[]>>({});
  const [detectingPhotoId, setDetectingPhotoId] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [markingPhotoId, setMarkingPhotoId] = useState<string | null>(null);
  const [markingBusy, setMarkingBusy] = useState(false);
  const [markingError, setMarkingError] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareModalMode, setShareModalMode] = useState<'share' | 'ask'>('share');
  const [lightboxPhoto, setLightboxPhoto] = useState<string | null>(null);
  const [publicUrl, setPublicUrl] = useState<string | null>(null);
  const [publicLoading, setPublicLoading] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);
  const [moderationTarget, setModerationTarget] = useState<{ commentId: string; mode: 'report' | 'block'; authorName: string; authorUserId: string | null } | null>(null);

  interface Comment { id: string; text: string; createdAt: string; userId: string | null; user: { id: string; name: string } | null; }
  const photoInputRef = useRef<HTMLInputElement>(null);
  const { detectFaces, describeFaceAtPoint } = useFaceDetection();

  useEffect(() => {
    api.get(`/events/${id}`)
      .then(({ data }) => {
        setEvent(data);
        if (data.publicToken && data.isPublic) {
          setPublicUrl(`${WEB_ORIGIN}/share/${data.publicToken}`);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    api.get('/account/members')
      .then(({ data }) => setMembers(Array.isArray(data) ? data : []))
      .catch(() => {});
    api.get(`/events/${id}/comments`)
      .then(({ data }) => setComments(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [id]);

  // Close picker when clicking outside
  useEffect(() => {
    if (!pickerOpenPhotoId) return;
    function handleClick(e: MouseEvent) {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-photo-picker]')) {
        setPickerOpenPhotoId(null);
        setPickerFaceIdx(null);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [pickerOpenPhotoId]);

  // Close lightbox on Escape
  useEffect(() => {
    if (!lightboxPhoto) return;
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setLightboxPhoto(null); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [lightboxPhoto]);

  // Replace, not push: stepping through 10 events shouldn't take 10 Backs to
  // return to the list — Back always goes straight to where the user came from.
  function goToEvent(targetId: string) {
    navigate(`/events/${targetId}`, { replace: true, state: location.state });
    window.scrollTo(0, 0);
  }

  // Anything open on top of the page owns the keyboard/gestures instead.
  const overlayOpen = Boolean(lightboxPhoto || showShareModal || moderationTarget || markingPhotoId || pickerOpenPhotoId);

  // ← / → step through the list (web).
  useEffect(() => {
    if (!navItems || overlayOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      const t = e.target as HTMLElement | null;
      if (t?.closest('input, textarea, select, audio, video, [contenteditable="true"]')) return;
      if (e.key === 'ArrowLeft' && prevItem) goToEvent(prevItem.id);
      if (e.key === 'ArrowRight' && nextItem) goToEvent(nextItem.id);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  // Swipe left/right to step through the list (phones, tablets, apps).
  function handleTouchStart(e: TouchEvent) {
    swipeStart.current = null;
    if (!navItems || overlayOpen || e.touches.length !== 1) return;
    const touch = e.touches[0];
    // Leave the screen edges to the OS back/forward gestures (Android, iOS).
    if (touch.clientX < 30 || touch.clientX > window.innerWidth - 30) return;
    if (swipeBlockedAt(e.target)) return;
    swipeStart.current = { x: touch.clientX, y: touch.clientY, t: Date.now() };
  }

  function handleTouchEnd(e: TouchEvent) {
    const start = swipeStart.current;
    swipeStart.current = null;
    if (!start || e.changedTouches.length !== 1) return;
    const dx = e.changedTouches[0].clientX - start.x;
    const dy = e.changedTouches[0].clientY - start.y;
    // A deliberate, mostly-horizontal flick — not a scroll or a slow drag.
    if (Math.abs(dx) < 70 || Math.abs(dy) > Math.abs(dx) * 0.5 || Date.now() - start.t > 600) return;
    if (dx < 0 && nextItem) goToEvent(nextItem.id);
    if (dx > 0 && prevItem) goToEvent(prevItem.id);
  }

  function openPicker(photoId: string, filePath: string) {
    setPickerOpenPhotoId(v => v === photoId ? null : photoId);
    setPickerFaceIdx(null);
    setMarkingPhotoId(null);
    setMarkingError(null);
    if (photoId in photoFaces) return;
    setDetectingPhotoId(photoId);
    detectFaces(filePath).then(faces => {
      setPhotoFaces(prev => ({
        ...prev,
        [photoId]: faces.map(f => ({ cropUrl: f.cropUrl, assignedUserId: null })),
      }));
      setDetectingPhotoId(null);
    }).catch(() => setDetectingPhotoId(null));
  }

  async function assignFaceToMember(photo: EventPhoto, faceIdx: number, userId: string) {
    const face = (photoFaces[photo.id] ?? [])[faceIdx];
    if (!face) return;
    setPhotoFaces(prev => ({
      ...prev,
      [photo.id]: (prev[photo.id] ?? []).map((f, fi) =>
        fi !== faceIdx ? f : { ...f, assignedUserId: userId }
      ),
    }));
    const alreadyTagged = (photo.participants ?? []).some(p => p.userId === userId);
    if (!alreadyTagged) await togglePhotoParticipant(photo, userId);
    setPickerFaceIdx(null);
  }

  async function handleMarkFace(photo: EventPhoto, xNorm: number, yNorm: number) {
    setMarkingBusy(true);
    setMarkingError(null);
    const face = await describeFaceAtPoint(photo.filePath, xNorm, yNorm);
    setMarkingBusy(false);
    if (!face) {
      setMarkingError("Couldn't find a face there — try tapping more precisely, or a different spot.");
      return;
    }
    const newIndex = (photoFaces[photo.id] ?? []).length;
    setPhotoFaces(prev => ({
      ...prev,
      [photo.id]: [...(prev[photo.id] ?? []), { cropUrl: face.cropUrl, assignedUserId: null }],
    }));
    setMarkingPhotoId(null);
    setMarkingError(null);
    setPickerFaceIdx(newIndex);
  }

  async function handlePhotoUpload(e: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []).slice(0, MAX_PHOTOS_PER_ADD);
    e.target.value = '';
    if (files.length === 0) return;
    setUploadError(null);
    const results = await runWithConcurrency(files, 3, async file => {
      const form = new FormData();
      form.append('photo', file);
      let takenAt: Date | null = null;
      let gps: { lat: number; lng: number } | null = null;
      try {
        const meta = await extractPhotoMetadata(file);
        takenAt = meta.takenAt;
        gps = meta.gps;
        if (takenAt) form.append('takenAt', takenAt.toISOString());
        if (gps) { form.append('gpsLat', String(gps.lat)); form.append('gpsLng', String(gps.lng)); }
      } catch { /* metadata is best-effort; upload proceeds without it */ }
      const caption = generatePhotoCaption({
        templateKind: getEventTemplate(event?.templateKind).kind,
        takenAt,
        location: event?.location ?? null,
        participantNames: [],
      });
      if (caption) form.append('caption', caption);
      const { data } = await api.post(`/events/${id}/photos`, form);
      setEvent(prev => prev ? { ...prev, photos: [...prev.photos, data] } : prev);
      return data;
    });
    const failures = results.flatMap(r => r.status === 'rejected' ? [serverErrorMessage(r.reason)] : []);
    if (failures.length > 0) setUploadError(describeUploadFailures(failures, files.length));
  }

  async function handleDeletePhoto(photoId: string) {
    await api.delete(`/events/${id}/photos/${photoId}`);
    setEvent(prev => prev ? { ...prev, photos: prev.photos.filter(p => p.id !== photoId) } : prev);
  }

  async function handleSaveCaption(photoId: string) {
    const { data } = await api.patch(`/events/${id}/photos/${photoId}`, { caption: editingCaptionValue });
    setEvent(prev => prev ? { ...prev, photos: prev.photos.map(p => p.id === photoId ? { ...p, caption: data.caption } : p) } : prev);
    setEditingCaptionId(null);
  }

  async function togglePhotoParticipant(photo: EventPhoto, userId: string) {
    const already = (photo.participants ?? []).some(p => p.userId === userId);
    if (already) {
      await api.delete(`/events/${id}/photos/${photo.id}/participants/${userId}`);
    } else {
      await api.post(`/events/${id}/photos/${photo.id}/participants`, { userId });
      // Also ensure event-level participant exists
      await api.post(`/events/${id}/participants`, { userId }).catch(() => {});
      const member = members.find(m => m.user.id === userId);
      if (member && !member.petType) {
        // Tagging by name (rather than tapping a detected face crop) — if
        // exactly one detected face in this photo is still unclaimed, it's a
        // safe, unambiguous bet that it's them, so mark it claimed in the UI
        // too (purely cosmetic now — there's no face reference being learned).
        const faces = photoFaces[photo.id] ?? [];
        const unassignedIdx = faces.reduce<number[]>((acc, f, fi) => (f.assignedUserId === null ? [...acc, fi] : acc), []);
        if (unassignedIdx.length === 1) {
          const faceIdx = unassignedIdx[0];
          setPhotoFaces(prev => ({
            ...prev,
            [photo.id]: (prev[photo.id] ?? []).map((f, fi) => fi !== faceIdx ? f : { ...f, assignedUserId: userId }),
          }));
        }
      }
    }
    // Refresh event
    const { data } = await api.get(`/events/${id}`);
    setEvent(data);
  }

  async function handleSaveAudio(blob: Blob, durationSec: number, label: string) {
    const form = new FormData();
    form.append('audio', blob, audioFileName(blob));
    if (label) form.append('label', label);
    form.append('durationSec', String(durationSec));
    const { data } = await api.post(`/audio/events/${id}`, form);
    setEvent(prev => prev ? { ...prev, audioClips: [...prev.audioClips, data] } : prev);
  }

  async function handleSaveLabel(clipId: string) {
    const { data } = await api.patch(`/audio/${clipId}`, { label: editingLabelValue });
    setEvent(prev => prev ? { ...prev, audioClips: prev.audioClips.map(a => a.id === clipId ? { ...a, label: data.label } : a) } : prev);
    setEditingLabelId(null);
    setEditingLabelValue('');
  }

  async function handleDeleteAudio(audioId: string) {
    await api.delete(`/audio/${audioId}`);
    setEvent(prev => prev ? { ...prev, audioClips: prev.audioClips.filter(a => a.id !== audioId) } : prev);
  }

  async function handleDeleteEvent() {
    if (!confirm('Delete this event permanently?')) return;
    await api.delete(`/events/${id}`);
    navigate('/events');
  }

  async function handleDismissContributionRequest() {
    if (!event?.myContributionRequest) return;
    const requestId = event.myContributionRequest.id;
    setEvent(prev => prev ? { ...prev, myContributionRequest: null } : prev);
    await contributionApi.dismiss(id!, requestId);
  }

  async function handlePublish() {
    setPublicLoading(true);
    try {
      const { data } = await api.post(`/events/${id}/publish`);
      setPublicUrl(data.publicUrl);
    } finally {
      setPublicLoading(false);
    }
  }

  async function handleUnpublish() {
    await api.delete(`/events/${id}/publish`);
    setPublicUrl(null);
  }

  function handleCopyLink() {
    if (!publicUrl) return;
    navigator.clipboard.writeText(publicUrl).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    });
  }

  async function handleAddComment(e: React.FormEvent) {
    e.preventDefault();
    if (!newComment.trim()) return;
    setCommentSubmitting(true);
    setCommentError(null);
    try {
      const { data } = await api.post(`/events/${id}/comments`, { text: newComment.trim() });
      setComments(prev => [...prev, data]);
      setNewComment('');
    } catch (err: any) {
      setCommentError(err?.response?.data?.error || 'Failed to post comment');
    } finally {
      setCommentSubmitting(false);
    }
  }

  async function handleDeleteComment(commentId: string) {
    await api.delete(`/events/${id}/comments/${commentId}`);
    setComments(prev => prev.filter(c => c.id !== commentId));
  }

  function handleModerationDone() {
    if (!moderationTarget) return;
    if (moderationTarget.mode === 'report') {
      setComments(prev => prev.filter(c => c.id !== moderationTarget.commentId));
    } else {
      setComments(prev => prev.filter(c => c.userId !== moderationTarget.authorUserId));
    }
    setModerationTarget(null);
  }

  if (loading) return <p className="text-slate-400 text-sm">Loading…</p>;
  if (!event) return <p className="text-red-500">Event not found.</p>;

  const activeMembers = members.filter(m => m.status === 'active' || m.status === 'no_email' || m.status === 'pending');
  const participantIds = new Set(event.participants.map(p => p.userId));
  const participantNames = members
    .filter(m => participantIds.has(m.user.id))
    .map(m => m.user.name);

  return (
    <div onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
      {/* Header */}
      <div className="mb-6 max-w-3xl">
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-3 flex-shrink-0">
            {navItems ? (
              // Arrived from a list: history -1 is that list, which restores its
              // own tab/sort/scroll position (see EventsList's saveListView).
              <button type="button" onClick={() => navigate(-1)} className="text-sm text-indigo-600 hover:underline">← Back</button>
            ) : (
              <Link to="/events" className="text-sm text-indigo-600 hover:underline">← Back</Link>
            )}
          </div>
          <div className="flex flex-wrap justify-end gap-1.5 flex-shrink-0">
            <button
              onClick={() => { setShareModalMode('share'); setShowShareModal(true); }}
              className="px-2.5 py-1.5 bg-indigo-50 text-indigo-700 rounded-lg text-sm hover:bg-indigo-100 transition-colors flex items-center gap-1"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"/>
              </svg>
              <span className="hidden sm:inline">Share</span>
            </button>
            {event.canEdit && (
              <button
                onClick={() => { setShareModalMode('ask'); setShowShareModal(true); }}
                className="px-2.5 py-1.5 bg-amber-50 text-amber-700 rounded-lg text-sm hover:bg-amber-100 transition-colors flex items-center gap-1"
                title="Ask a full-access person to help complete this event"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                </svg>
                <span className="hidden sm:inline">Ask to contribute</span>
              </button>
            )}
            <button
              onClick={publicUrl ? handleCopyLink : handlePublish}
              disabled={publicLoading}
              className="px-2.5 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-sm hover:bg-emerald-100 transition-colors flex items-center gap-1 disabled:opacity-50"
              title={publicUrl ? 'Copy public link' : 'Generate public link'}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/>
              </svg>
              <span className="hidden sm:inline">{copySuccess ? 'Copied!' : publicUrl ? 'Copy link' : 'Public link'}</span>
            </button>
            {publicUrl && (
              <button
                onClick={handleUnpublish}
                className="px-2 py-1.5 bg-slate-50 text-slate-400 rounded-lg text-sm hover:bg-red-50 hover:text-red-600 transition-colors flex items-center"
                title="Revoke public link — makes it private again"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3M4 7h16"/>
                </svg>
              </button>
            )}
            {event.canEdit && (
              <>
                <Link
                  to={`/events/${id}/edit`}
                  className="px-2.5 py-1.5 bg-slate-100 text-slate-700 rounded-lg text-sm hover:bg-slate-200 transition-colors"
                >
                  Edit
                </Link>
                <button
                  onClick={handleDeleteEvent}
                  className="px-2.5 py-1.5 bg-red-50 text-red-700 rounded-lg text-sm hover:bg-red-100 transition-colors"
                >
                  Delete
                </button>
              </>
            )}
          </div>
        </div>
        {/* Previous/Next through the list this event was opened from */}
        {navItems && navIndex >= 0 && (
          <nav className="mb-4 grid grid-cols-[1fr_auto_1fr] items-stretch gap-2" aria-label="More memories">
            {prevItem ? (
              <button
                type="button"
                onClick={() => goToEvent(prevItem.id)}
                aria-label={`Previous: ${prevItem.title}`}
                className="text-left rounded-xl border border-indigo-100 bg-white px-3 py-2 hover:border-indigo-300 hover:bg-indigo-50/50 transition-colors min-w-0"
              >
                <span className="block text-[11px] text-slate-400">‹ Previous</span>
                <span className="block text-sm font-medium text-slate-700 truncate">{prevItem.title}</span>
              </button>
            ) : <span />}
            <span className="self-center px-1 text-xs text-slate-400 tabular-nums whitespace-nowrap">{navIndex + 1} of {navItems.length}</span>
            {nextItem ? (
              <button
                type="button"
                onClick={() => goToEvent(nextItem.id)}
                aria-label={`Next: ${nextItem.title}`}
                className="text-right rounded-xl border border-indigo-100 bg-white px-3 py-2 hover:border-indigo-300 hover:bg-indigo-50/50 transition-colors min-w-0"
              >
                <span className="block text-[11px] text-slate-400">Next ›</span>
                <span className="block text-sm font-medium text-slate-700 truncate">{nextItem.title}</span>
              </button>
            ) : <span />}
          </nav>
        )}
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
          {event.templateKind && event.templateKind !== 'general' && (
            <span aria-hidden="true">{getEventTemplate(event.templateKind).icon}</span>
          )}
          {event.title}
        </h1>
        <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1 text-sm text-slate-500">
          {event.eventDate && <span>{format(parseEventDate(event.eventDate), 'MMMM d, yyyy')}</span>}
          {event.location && <span>{event.location}</span>}
        </div>
        {participantNames.length > 0 && (
          <p className="text-sm text-slate-500 mt-1">With: {participantNames.join(', ')}</p>
        )}
      </div>

      {event.description && (
        <p className="text-slate-600 mb-8 leading-relaxed max-w-3xl whitespace-pre-wrap">{event.description}</p>
      )}

      {event.canEdit && event.contributionRequests && event.contributionRequests.length > 0 && (
        <div className="mb-8 max-w-3xl bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
          <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-2">Help requested</p>
          <ul className="space-y-1.5">
            {event.contributionRequests.map(r => (
              <li key={r.id} className={`text-sm ${r.accessOrphaned ? 'text-slate-300' : 'text-slate-600'}`}>
                <span className="font-medium">{r.member.user.name}</span>
                {r.fulfillment.photos && <> · {r.fulfillment.photos.added} of {r.fulfillment.photos.requested} photos</>}
                {r.fulfillment.voice && <> · voice {r.fulfillment.voice.complete ? '✓' : 'not yet'}</>}
                {r.fulfillment.context && <> · context {r.fulfillment.context.complete ? '✓' : 'not yet'}</>}
                {r.accessOrphaned && <span className="italic"> — access changed, no longer active</span>}
              </li>
            ))}
          </ul>
          <button
            onClick={() => { setShareModalMode('ask'); setShowShareModal(true); }}
            className="mt-2 text-xs font-semibold text-indigo-600 hover:underline"
          >
            Edit or ask someone else
          </button>
        </div>
      )}

      {event.myContributionRequest && (() => {
        const req = event.myContributionRequest;
        const allComplete = [req.fulfillment.photos, req.fulfillment.voice, req.fulfillment.context]
          .filter((f): f is NonNullable<typeof f> => f != null)
          .every(f => f.complete);
        return (
          <div className="mb-8 max-w-3xl bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-3.5">
            <div className="flex items-start justify-between gap-3">
              <p className="text-sm text-slate-700 leading-relaxed">
                {allComplete ? "You're all set — thanks for adding to this memory!" : req.askSummary}
              </p>
              <button
                onClick={handleDismissContributionRequest}
                className="text-slate-400 hover:text-slate-600 flex-shrink-0"
                title="Dismiss"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
            {!allComplete && (
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                {req.fulfillment.photos && (
                  <span>{req.fulfillment.photos.added} of {req.fulfillment.photos.requested} photos{req.fulfillment.photos.complete ? ' ✓' : ''}</span>
                )}
                {req.fulfillment.voice && (
                  <span>voice {req.fulfillment.voice.complete ? '✓' : 'not yet'}</span>
                )}
                {req.fulfillment.context && (
                  <span>context {req.fulfillment.context.complete ? '✓' : 'not yet'}</span>
                )}
              </div>
            )}
            {!allComplete && (
              <div className="mt-3 flex gap-2">
                {req.fulfillment.photos && !req.fulfillment.photos.complete && (
                  <button
                    onClick={() => { preloadFaceModels(); photoInputRef.current?.click(); }}
                    className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-semibold hover:bg-indigo-700 transition-colors"
                  >
                    + Add Photo
                  </button>
                )}
                {req.fulfillment.voice && !req.fulfillment.voice.complete && (
                  <button
                    onClick={() => document.getElementById('voice-stories')?.scrollIntoView({ behavior: 'smooth' })}
                    className="px-3 py-1.5 bg-white text-indigo-700 border border-indigo-200 rounded-lg text-xs font-semibold hover:bg-indigo-50 transition-colors"
                  >
                    Record a Memory
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* Photos */}
      <section className="mb-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-slate-700">Photos</h2>
          <button
            onClick={() => { preloadFaceModels(); photoInputRef.current?.click(); }}
            className="text-sm text-indigo-600 hover:underline"
          >
            + Add Photo
          </button>
          <input ref={photoInputRef} type="file" accept={PHOTO_ACCEPT} multiple className="hidden" onChange={handlePhotoUpload} />
        </div>
        {uploadError && (
          <p className="text-sm text-red-500 mb-3">{uploadError}</p>
        )}

        {event.photos.length === 0 ? (
          <p className="text-slate-400 text-sm">No photos yet.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {event.photos.map(photo => {
              const isPickerOpen = pickerOpenPhotoId === photo.id;
              const taggedIds = new Set((photo.participants ?? []).map(p => p.userId));
              const isMarking = isPickerOpen && markingPhotoId === photo.id;

              return (
                <div key={photo.id} className="rounded-xl border border-indigo-50 bg-white shadow-sm overflow-hidden">
                  {/* Photo image — click to open lightbox */}
                  <div className="relative group aspect-square bg-slate-100">
                    <img
                      src={photo.filePath}
                      alt=""
                      loading="lazy"
                      className="w-full h-full object-cover cursor-zoom-in"
                      onClick={() => setLightboxPhoto(photo.filePath)}
                    />
                    <button
                      onClick={e => { e.stopPropagation(); handleDeletePhoto(photo.id); }}
                      className="absolute top-1.5 right-1.5 w-8 h-8 rounded-full bg-black/60 text-white text-sm flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity leading-none"
                    >
                      ×
                    </button>
                  </div>

                  {/* Marking overlay — enlarged photo so the user can tap a face
                      precisely; the grid thumbnail is too small to hit accurately,
                      especially on mobile. Same normalized-coordinate math as
                      before, just against a much bigger rendered image. */}
                  {isMarking && (
                    <div
                      className="fixed inset-0 z-50 bg-black/90 flex flex-col items-center justify-center p-4"
                      onClick={() => { setMarkingPhotoId(null); setMarkingError(null); }}
                    >
                      <img
                        src={photo.filePath}
                        alt=""
                        className="max-w-[95vw] max-h-[75vh] object-contain cursor-crosshair select-none"
                        onClick={e => {
                          e.stopPropagation();
                          const rect = e.currentTarget.getBoundingClientRect();
                          handleMarkFace(photo, (e.clientX - rect.left) / rect.width, (e.clientY - rect.top) / rect.height);
                        }}
                      />
                      <div className="mt-4 text-center max-w-sm">
                        <p className="text-sm text-white font-medium">
                          {markingBusy ? 'Looking for a face there…' : 'Tap their face'}
                        </p>
                        {markingError && <p className="text-xs text-red-300 mt-1">{markingError}</p>}
                      </div>
                      <button
                        onClick={e => { e.stopPropagation(); setMarkingPhotoId(null); setMarkingError(null); }}
                        aria-label="Cancel marking"
                        className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white text-xl flex items-center justify-center transition-colors"
                      >
                        ×
                      </button>
                    </div>
                  )}

                  {/* Person tags */}
                  <div className="px-2.5 py-2" data-photo-picker>
                    <div className="flex flex-wrap gap-1 items-center">
                      {(photo.participants ?? []).map(p => (
                        <span
                          key={p.userId}
                          className="inline-flex items-center gap-1 text-xs bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded-full font-medium"
                        >
                          {(p.user.name ?? '?').split(' ')[0]}
                          <button
                            onClick={() => togglePhotoParticipant(photo, p.userId)}
                            className="text-indigo-400 hover:text-indigo-700 leading-none ml-0.5"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                      {activeMembers.length > 0 && (
                        <button
                          onClick={() => openPicker(photo.id, photo.filePath)}
                          className="text-xs text-slate-400 hover:text-indigo-600 px-1.5 py-0.5 rounded-full border border-dashed border-slate-200 hover:border-indigo-300 transition-colors"
                        >
                          {isPickerOpen ? '× close' : '+ Tag'}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Caption — quiet by default (see backward-compat rule: no
                      always-open empty input on legacy photos), tap to edit */}
                  <div className="px-2.5 pb-2">
                    {editingCaptionId === photo.id ? (
                      <div className="flex items-center gap-1.5">
                        <input
                          type="text"
                          autoFocus
                          value={editingCaptionValue}
                          onChange={e => setEditingCaptionValue(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') handleSaveCaption(photo.id); if (e.key === 'Escape') setEditingCaptionId(null); }}
                          className="flex-1 text-xs border border-indigo-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                        <button
                          onClick={() => handleSaveCaption(photo.id)}
                          className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                        >
                          Save
                        </button>
                      </div>
                    ) : photo.caption ? (
                      <button
                        onClick={() => { setEditingCaptionId(photo.id); setEditingCaptionValue(photo.caption ?? ''); }}
                        className="text-xs text-slate-500 hover:text-indigo-600 text-left"
                      >
                        {photo.caption}
                      </button>
                    ) : (
                      <button
                        onClick={() => { setEditingCaptionId(photo.id); setEditingCaptionValue(''); }}
                        className="text-xs text-slate-300 hover:text-indigo-600"
                      >
                        + Add caption
                      </button>
                    )}
                  </div>

                  {/* Inline face/tag picker — expands within the card, no overflow issues */}
                  {isPickerOpen && (() => {
                    const faces = photoFaces[photo.id] ?? [];
                    const isDetecting = detectingPhotoId === photo.id;
                    const faceMembers = activeMembers.filter(m => !m.petType);
                    const petMembers = activeMembers.filter(m => m.petType);
                    const selectedFace = pickerFaceIdx !== null ? faces[pickerFaceIdx] : null;
                    const faceAssignedIds = new Set(faces.map(f => f.assignedUserId).filter(Boolean) as string[]);
                    const undetectedMembers = faceMembers.filter(m => !faceAssignedIds.has(m.user.id));

                    return (
                      <div className="border-t border-indigo-50 px-2.5 py-2.5 max-h-72 overflow-y-auto" data-photo-picker>
                        {isDetecting && (
                          <p className="text-xs text-violet-600 flex items-center gap-1.5 mb-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-violet-500 animate-pulse" />
                            Detecting faces…
                          </p>
                        )}

                        {!isDetecting && faces.length === 0 && !isMarking && (
                          <div className="mb-2.5 pb-2.5 border-b border-slate-100">
                            <button
                              type="button"
                              onClick={() => { setMarkingPhotoId(photo.id); setMarkingError(null); }}
                              className="text-xs font-medium text-indigo-600 hover:text-indigo-800"
                            >
                              No face detected automatically — tap on the photo to mark it
                            </button>
                          </div>
                        )}

                        {!isDetecting && faces.length > 0 && pickerFaceIdx === null && (
                          <div className="mb-2.5">
                            <p className="text-[11px] font-medium text-slate-500 mb-1.5">
                              {faces.length === 1 ? '1 face — tap to identify' : `${faces.length} faces — tap to identify`}
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {faces.map((face, fi) => {
                                const assigned = face.assignedUserId
                                  ? members.find(m => m.user.id === face.assignedUserId)
                                  : null;
                                return (
                                  <button
                                    key={fi}
                                    type="button"
                                    onClick={e => { e.stopPropagation(); setPickerFaceIdx(fi); }}
                                    className={`flex flex-col items-center gap-0.5 p-1 rounded-lg border-2 transition-all ${
                                      face.assignedUserId ? 'border-indigo-400 bg-indigo-50' : 'border-dashed border-slate-300 hover:border-indigo-300'
                                    }`}
                                  >
                                    <img src={face.cropUrl} className="w-10 h-10 rounded object-cover" alt="" />
                                    <span className="text-[10px] max-w-[44px] truncate font-medium text-slate-700">
                                      {assigned ? (assigned.user.name ?? '?').split(' ')[0] : '?'}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                            {(undetectedMembers.length > 0 || petMembers.length > 0) && (
                              <p className="text-[11px] font-medium text-slate-400 mt-2 mb-1">Also tag manually:</p>
                            )}
                          </div>
                        )}

                        {/* Face assignment mode */}
                        {!isDetecting && selectedFace !== null && (
                          <div className="mb-2">
                            <div className="flex items-center gap-2 mb-2">
                              <button
                                type="button"
                                onClick={() => setPickerFaceIdx(null)}
                                className="text-xs text-slate-400 hover:text-slate-600"
                              >
                                ←
                              </button>
                              <img src={selectedFace.cropUrl} className="w-8 h-8 rounded object-cover" alt="" />
                              <p className="text-xs font-medium text-slate-600">Who is this?</p>
                            </div>
                            <div className="space-y-0.5">
                              {faceMembers.map(m => {
                                const isAssigned = selectedFace.assignedUserId === m.user.id;
                                return (
                                  <button
                                    key={m.user.id}
                                    type="button"
                                    onClick={() => assignFaceToMember(photo, pickerFaceIdx!, m.user.id)}
                                    className={`w-full text-left text-sm px-2.5 py-1.5 rounded-lg flex items-center gap-2 transition-colors ${
                                      isAssigned ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-slate-600 hover:bg-slate-50'
                                    }`}
                                  >
                                    <span className={`w-4 h-4 rounded-full flex items-center justify-center text-xs flex-shrink-0 ${
                                      isAssigned ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-400'
                                    }`}>
                                      {isAssigned ? '✓' : (m.user.name?.[0] ?? '?').toUpperCase()}
                                    </span>
                                    {m.user.name}
                                  </button>
                                );
                              })}
                            </div>
                            <div className="border-t border-slate-100 mt-1 pt-1" />
                          </div>
                        )}

                        {/* Regular member list (undetected people + pets, or when no faces detected) */}
                        {pickerFaceIdx === null && (
                          <>
                            {(!faces.length && !isDetecting) && (
                              <p className="text-xs font-medium text-slate-500 px-1 mb-1.5">Who's in this photo?</p>
                            )}
                            <div className="space-y-0.5">
                              {(faces.length > 0 ? undetectedMembers : faceMembers).map(m => {
                                const tagged = taggedIds.has(m.user.id);
                                return (
                                  <button
                                    key={m.user.id}
                                    onClick={() => togglePhotoParticipant(photo, m.user.id)}
                                    className={`w-full text-left text-sm px-2.5 py-1.5 rounded-lg flex items-center gap-2 transition-colors ${
                                      tagged ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-slate-600 hover:bg-slate-50'
                                    }`}
                                  >
                                    <span className={`w-4 h-4 rounded-full flex items-center justify-center text-xs flex-shrink-0 ${
                                      tagged ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-400'
                                    }`}>
                                      {tagged ? '✓' : (m.user.name?.[0] ?? '?').toUpperCase()}
                                    </span>
                                    {m.user.name}
                                  </button>
                                );
                              })}
                              {petMembers.map(m => {
                                const tagged = taggedIds.has(m.user.id);
                                return (
                                  <button
                                    key={m.user.id}
                                    onClick={() => togglePhotoParticipant(photo, m.user.id)}
                                    className={`w-full text-left text-sm px-2.5 py-1.5 rounded-lg flex items-center gap-2 transition-colors ${
                                      tagged ? 'bg-amber-50 text-amber-700 font-medium' : 'text-slate-600 hover:bg-slate-50'
                                    }`}
                                  >
                                    <span className="text-base leading-none">🐾</span>
                                    {m.user.name}
                                  </button>
                                );
                              })}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Audio */}
      <section id="voice-stories" className="max-w-3xl">
        <h2 className="text-base font-semibold text-slate-700 mb-4">Voice Stories</h2>
        {event.audioClips.length > 0 && (
          <div className="space-y-3 mb-4">
            {event.audioClips.map(clip => (
              <div key={clip.id} className="bg-indigo-50 rounded-xl p-3 flex items-center gap-3">
                <div className="flex-1">
                  {editingLabelId === clip.id ? (
                    <div className="flex gap-2 mb-2">
                      <input
                        autoFocus
                        type="text"
                        value={editingLabelValue}
                        onChange={e => setEditingLabelValue(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleSaveLabel(clip.id); if (e.key === 'Escape') setEditingLabelId(null); }}
                        placeholder="e.g. Grandma's story"
                        className="flex-1 border border-indigo-300 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <button onClick={() => handleSaveLabel(clip.id)} className="px-2.5 py-1 bg-indigo-600 text-white text-xs rounded-lg hover:bg-indigo-700">Save</button>
                      <button onClick={() => setEditingLabelId(null)} className="px-2.5 py-1 text-slate-500 text-xs rounded-lg hover:bg-slate-200">Cancel</button>
                    </div>
                  ) : (
                    <button
                      onClick={() => { setEditingLabelId(clip.id); setEditingLabelValue(clip.label ?? ''); }}
                      className="text-sm font-medium text-slate-700 mb-1 hover:text-indigo-600 flex items-center gap-1 group"
                    >
                      {clip.label ?? <span className="text-slate-400 font-normal">Add label…</span>}
                      <svg className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M9 13l6.586-6.586a2 2 0 012.828 2.828L11.828 15.828a2 2 0 01-1.414.586H9v-2a2 2 0 01.586-1.414z"/></svg>
                    </button>
                  )}
                  <audio src={clip.filePath} controls className="w-full h-8" />
                  {clip.durationSec && <p className="text-xs text-slate-400 mt-1">{clip.durationSec}s</p>}
                </div>
                <button
                  onClick={() => handleDeleteAudio(clip.id)}
                  className="min-w-[36px] min-h-[36px] flex items-center justify-center text-slate-400 hover:text-red-500 text-lg leading-none rounded"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="space-y-2">
          <AudioRecorder onSave={handleSaveAudio} />
        </div>
      </section>

      {/* Public link panel */}
      {publicUrl && (
        <section className="max-w-3xl mb-8">
          <div className="bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3 flex items-center gap-3 flex-wrap">
            <svg className="w-4 h-4 text-emerald-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/>
            </svg>
            <span className="text-sm text-emerald-800 flex-1 min-w-0 truncate">{publicUrl}</span>
            <div className="flex gap-2 flex-shrink-0">
              <button onClick={handleCopyLink} className="text-xs font-medium text-emerald-700 hover:text-emerald-900 px-2 py-1 rounded bg-emerald-100 hover:bg-emerald-200 transition-colors">
                {copySuccess ? 'Copied!' : 'Copy'}
              </button>
              <button onClick={handleUnpublish} className="text-xs text-slate-400 hover:text-red-500 transition-colors">Revoke</button>
            </div>
          </div>
        </section>
      )}

      {/* Comments */}
      <section className="max-w-3xl mb-10">
        <h2 className="text-base font-semibold text-slate-700 mb-4">Comments</h2>
        {comments.length > 0 && (
          <div className="space-y-3 mb-4">
            {comments.map(c => (
              <div key={c.id} className="flex gap-3 group">
                <div className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-600 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                  {(c.user?.name ?? 'A')[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm font-medium text-slate-800">{c.user?.name ?? 'Anonymous'}</span>
                    <span className="text-xs text-slate-400">{new Date(c.createdAt).toLocaleDateString()}</span>
                  </div>
                  <p className="text-sm text-slate-600 mt-0.5 whitespace-pre-wrap">{c.text}</p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {c.userId && c.userId !== user?.id && (
                    <>
                      <button
                        onClick={() => setModerationTarget({ commentId: c.id, mode: 'report', authorName: c.user?.name ?? 'this user', authorUserId: c.userId })}
                        className="min-h-[36px] px-1.5 flex items-center justify-center text-slate-300 hover:text-red-400 text-xs transition-colors"
                      >Report</button>
                      <button
                        onClick={() => setModerationTarget({ commentId: c.id, mode: 'block', authorName: c.user?.name ?? 'this user', authorUserId: c.userId })}
                        className="min-h-[36px] px-1.5 flex items-center justify-center text-slate-300 hover:text-red-400 text-xs transition-colors"
                      >Block</button>
                    </>
                  )}
                  <button
                    onClick={() => handleDeleteComment(c.id)}
                    className="min-w-[36px] min-h-[36px] flex items-center justify-center text-slate-300 hover:text-red-400 text-sm transition-colors"
                  >×</button>
                </div>
              </div>
            ))}
          </div>
        )}
        {commentError && (
          <p className="text-sm text-red-500 mb-2">{commentError}</p>
        )}
        <form onSubmit={handleAddComment} className="flex gap-2">
          <input
            type="text"
            value={newComment}
            onChange={e => setNewComment(e.target.value)}
            placeholder="Add a comment…"
            className="flex-1 border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <button
            type="submit"
            disabled={!newComment.trim() || commentSubmitting}
            className="px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            Post
          </button>
        </form>
      </section>

      {showShareModal && event && (
        <ShareEventModal
          eventId={event.id}
          eventTitle={event.title}
          initialMode={shareModalMode}
          onClose={() => setShowShareModal(false)}
        />
      )}

      {moderationTarget && event && (
        <ReportOrBlockModal
          mode={moderationTarget.mode}
          eventId={event.id}
          commentId={moderationTarget.commentId}
          authorName={moderationTarget.authorName}
          authorUserId={moderationTarget.authorUserId}
          onClose={() => setModerationTarget(null)}
          onDone={handleModerationDone}
        />
      )}

      {/* Lightbox */}
      {lightboxPhoto && (
        <div
          className="fixed inset-0 z-50 bg-black/92 flex items-center justify-center"
          onClick={() => setLightboxPhoto(null)}
        >
          <img
            src={lightboxPhoto}
            alt=""
            className="max-w-[95vw] max-h-[95vh] object-contain cursor-zoom-out select-none"
          />
          <button
            onClick={() => setLightboxPhoto(null)}
            className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white text-xl flex items-center justify-center transition-colors"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}
